import {
  ClipboardEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import ReactInputMask from 'react-input-mask';
import { WithContext as ReactTags } from 'react-tag-input';
import { ReactTag } from 'types';
import * as S from './styles';

interface Props {
  id: string;
  value: string;
  tags: ReactTag[];
  setTags: Dispatch<SetStateAction<ReactTag[]>>;
  handleChange: (value: string) => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
}

const TAG_SEPARATORS = [',', ';', ' ', '\n', '\r'];
const KEY_CODES = {
  tab: 9,
  enter: 13,
  space: 32,
  semicolon: 186,
  comma: 188,
};

export function Input({
  id,
  value,
  handleChange,
  handleSubmit,
  tags,
  setTags,
}: Props) {
  const horizontalScrollRef = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    const { current } = horizontalScrollRef;

    if (!current) return;

    function sideScroll(event: any) {
      event.preventDefault();

      current?.scroll({ behavior: 'smooth' });
      current?.scrollBy({ left: event.deltaY / 3, behavior: 'smooth' });
    }

    current.addEventListener('wheel', sideScroll);

    return () => current.removeEventListener('wheel', sideScroll);
  }, [horizontalScrollRef]);

  const renderDefaultInput = (
    <input
      id={id}
      autoComplete="off"
      type="text"
      name={id}
      autoFocus
      placeholder="Buscar..."
      value={value}
      onChange={(event) => {
        handleChange(event.target.value);
      }}
    />
  );

  const renderCpfInput = (
    <ReactInputMask
      placeholder="Buscar..."
      type="tel"
      id={id}
      name={id}
      autoFocus
      onChange={(event) => {
        handleChange(event.target.value);
      }}
      value={value}
      mask="999.999.999-99"
      maskChar={null}
    />
  );

  function onPasteTag(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();

    const clipboardText = event.clipboardData.getData('Text');

    const newTags = clipboardText
      .split(new RegExp(TAG_SEPARATORS.join('|')))
      .filter((text) => text !== '')
      .map((text) => {
        const formattedText = text.trim();

        return { id: formattedText, text: formattedText };
      });

    const validNewTags = newTags.filter((newTag) => {
      return tags.every((tag) => tag.id !== newTag.id);
    });

    if (!validNewTags.length) return;

    setTags([...tags, ...validNewTags]);
    handleSubmit();
  }

  function handleDeleteTag(i: number) {
    const newTags = tags.filter((_, index) => index !== i);

    if (newTags.length === 0 && window.location.hash.includes('#')) {
      window.history.pushState('', '', '/');
    }

    setTags(newTags);
    handleSubmit();
  }

  function handleAdditionTag(tag: ReactTag) {
    handleChange('');

    setTags([...tags, tag]);
    handleSubmit();
  }

  const renderTagsInput = (
    <S.InputTags onPaste={onPasteTag}>
      <ReactTags
        classNames={{
          tag: 'tag',
          tagInput: 'input-width',
          tags: 'input-width',
          selected: 'input-width',
        }}
        placeholder="Buscar..."
        id={id}
        tags={tags}
        handleDelete={handleDeleteTag}
        handleAddition={handleAdditionTag}
        inputValue={value}
        handleInputChange={(textInputValue) => handleChange(textInputValue)}
        allowDragDrop={false}
        allowAdditionFromPaste={false}
        minQueryLength={4}
        delimiters={[
          KEY_CODES.tab,
          KEY_CODES.enter,
          KEY_CODES.space,
          KEY_CODES.semicolon,
          KEY_CODES.comma,
        ]}
      />
    </S.InputTags>
  );

  function renderInput() {
    switch (id) {
      case 'cpf':
        return renderCpfInput;

      case 'codes':
        return renderTagsInput;

      default:
        return renderDefaultInput;
    }
  }

  return (
    <S.Wrapper htmlFor={id} ref={horizontalScrollRef}>
      {renderInput()}
    </S.Wrapper>
  );
}
